import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 Sekunde

/**
 * Hook für PDF-Generierung mit Typst Backend
 * Enthält Retry-Logik und verbesserte Fehlerbehandlung
 *
 * @param {Object} ergebnis - Das Korrekturrechnung-Ergebnis mit allen Rechnungsdaten
 * @returns {Object} - PDF-Funktionen und Loading-State
 */
export const usePdfGenerator = (ergebnis, customRechnungsNr = null) => {
  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * Generiert den PDF-Dateinamen im Format: JJJJ-MM-KR-Nachname-Vorname-BA.pdf
   */
  const generateFilename = useCallback((variant = "ba") => {
    const zusammenfassung = ergebnis?.zusammenfassung;
    if (!zusammenfassung) return `Korrekturrechnung-${variant}.pdf`;

    // Abrechnungsmonat extrahieren (Format: "01.03.2025 - 31.03.2025")
    let year = new Date().getFullYear();
    let month = String(new Date().getMonth() + 1).padStart(2, '0');

    if (zusammenfassung.abrechnungszeitraum) {
      const match = zusammenfassung.abrechnungszeitraum.match(/(\d{2})\.(\d{2})\.(\d{4})/);
      if (match) {
        month = match[2];
        year = match[3];
      }
    }

    // Klientenname extrahieren
    const klient = zusammenfassung.klient || "";
    const nameParts = klient.split(" ");
    const vorname = nameParts[0] || "";
    const nachname = nameParts.slice(1).join(" ") || nameParts[0] || "Unbekannt";

    // Format: 2026-01-KR-Nachname-Vorname-BA.pdf
    const suffix = variant === "privat" ? "-Privat" : "-BA";
    return `${year}-${month}-KR-${nachname}-${vorname}${suffix}.pdf`;
  }, [ergebnis?.zusammenfassung]);

  /**
   * Extrahiert den Abrechnungsmonat für SharePoint (Format: "2026-01")
   */
  const getAbrechnungsmonat = useCallback(() => {
    const zusammenfassung = ergebnis?.zusammenfassung;
    if (!zusammenfassung?.abrechnungszeitraum) {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }

    const match = zusammenfassung.abrechnungszeitraum.match(/(\d{2})\.(\d{2})\.(\d{4})/);
    if (match) {
      return `${match[3]}-${match[2]}`;
    }
    return `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  }, [ergebnis?.zusammenfassung]);

  /**
   * Hilfsfunktion für Retry mit Verzögerung
   */
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  /**
   * Generiert PDF über Typst Backend mit Retry-Logik
   * @param {string} variant - "ba" oder "privat"
   * @param {number} retryCount - Aktuelle Anzahl der Versuche
   * @returns {Promise<{base64: string, filename: string, blob: Blob} | null>}
   */
  const generatePdf = useCallback(async (variant = "ba", retryCount = 0) => {
    if (!ergebnis) {
      toast.error("Keine Korrekturrechnung vorhanden");
      return null;
    }

    const filename = generateFilename(variant);
    setIsGenerating(true);

    try {
      // Daten für Typst-Backend vorbereiten
      // Verwende customRechnungsNr wenn vorhanden, sonst originalRechnung.rechnungsNr
      const rechnungsNr = customRechnungsNr || ergebnis.originalRechnung?.rechnungsNr || "";
      
      const pdfData = {
        originalRechnung: {
          ...ergebnis.originalRechnung,
          rechnungsNr: variant === "privat" ? `${rechnungsNr}-P` : rechnungsNr
        },
        baRechnung: {
          ...ergebnis.baRechnung,
          rechnungsNr: rechnungsNr
        },
        privatRechnung: ergebnis.privatRechnung,
        positionen: ergebnis.positionen,
        zusammenfassung: {
          ...ergebnis.zusammenfassung,
          rechnungsNr: rechnungsNr
        }
      };

      // PDF vom Backend generieren lassen
      const response = await axios.post(
        `${BACKEND_URL}/api/rechnung/pdf/typst/preview?typ=${variant}`,
        pdfData,
        {
          responseType: 'blob',
          timeout: 30000, // 30 Sekunden Timeout
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      // Prüfe ob Response gültige PDF-Daten enthält
      if (!response.data || response.data.size === 0) {
        throw new Error("Leere PDF-Antwort vom Server");
      }

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });

      // Blob zu Base64 konvertieren
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          setIsGenerating(false);
          resolve({ base64, filename, blob: pdfBlob });
        };
        reader.onerror = () => {
          setIsGenerating(false);
          reject(new Error("Fehler beim Lesen der PDF-Daten"));
        };
        reader.readAsDataURL(pdfBlob);
      });
    } catch (error) {
      // Retry bei Netzwerkfehlern
      const isNetworkError = !error.response || error.code === 'ECONNABORTED';
      const isServerError = error.response?.status >= 500;

      if ((isNetworkError || isServerError) && retryCount < MAX_RETRIES) {
        console.warn(`PDF-Generierung fehlgeschlagen, Retry ${retryCount + 1}/${MAX_RETRIES}...`);
        await sleep(RETRY_DELAY * (retryCount + 1)); // Exponentielles Backoff
        return generatePdf(variant, retryCount + 1);
      }

      setIsGenerating(false);

      // Detaillierte Fehlermeldungen
      let errorMessage = "PDF-Generierung fehlgeschlagen";
      if (error.response?.status === 400) {
        errorMessage = "Ungültige Rechnungsdaten";
      } else if (error.response?.status === 404) {
        errorMessage = "PDF-Endpoint nicht erreichbar";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server-Fehler bei PDF-Generierung";
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = "Zeitüberschreitung bei PDF-Generierung";
      } else if (!error.response) {
        errorMessage = "Keine Verbindung zum Server";
      }

      console.error("PDF-Generierung fehlgeschlagen:", error);
      toast.error(`${errorMessage}: ${error.response?.data?.detail || error.message}`);
      return null;
    }
  }, [ergebnis, generateFilename, customRechnungsNr]);

  /**
   * Download PDF direkt als Datei
   * @param {string} variant - "ba" oder "privat"
   */
  const downloadPdf = useCallback(async (variant = "ba") => {
    const toastId = toast.loading("PDF wird generiert...");
    const result = await generatePdf(variant);
    toast.dismiss(toastId);

    if (result) {
      // Download triggern
      const link = document.createElement('a');
      link.href = URL.createObjectURL(result.blob);
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href); // Memory cleanup
      toast.success(`${result.filename} heruntergeladen`);
      return true;
    }
    return false;
  }, [generatePdf]);

  /**
   * PDF in neuem Tab öffnen (Vorschau)
   * @param {string} variant - "ba" oder "privat"
   */
  const previewPdf = useCallback(async (variant = "ba") => {
    const toastId = toast.loading("PDF-Vorschau wird erstellt...");
    const result = await generatePdf(variant);
    toast.dismiss(toastId);

    if (result) {
      const url = URL.createObjectURL(result.blob);
      window.open(url, '_blank');
      // Cleanup nach 1 Minute (gibt Zeit zum Laden)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return true;
    }
    return false;
  }, [generatePdf]);

  return {
    generatePdf,
    downloadPdf,
    previewPdf,
    generateFilename,
    getAbrechnungsmonat,
    isGenerating
  };
};

export default usePdfGenerator;
